import { yupResolver } from "@hookform/resolvers/yup";
import {
  Alert,
  Autocomplete,
  Button,
  FormControlLabel,
  Grid,
  Switch,
  TextField,
} from "@mui/material";
import { useEffect } from "react";
import { Controller, useForm } from "react-hook-form";
import * as yup from "yup";

import { Company } from "../../../../../types/company";
import { ShipmentProps } from "../../../../../types/shipment";

let customRegimeScheme = yup.object({
  ID_CENTRO: yup.number().required(),
  DSC_INSTRUCAO: yup.string().max(200).required(),
  ATIVO: yup.boolean().notRequired(),
});

interface ShipmentFormProps {
  shipment: ShipmentProps;
  center: any[];
  companies: Company[];
  onSubmit: (customRegime: ShipmentProps) => void;
  onCancel: () => void;
  isLoading?: boolean;
  errors?: string[];
  controlEdit: {
    loading: boolean;
    isInUse: boolean;
  };
  readOnly: boolean;
}

export default function Shipment({
  shipment,
  center,
  onSubmit,
  onCancel,
  isLoading = false,
  errors = [],
  controlEdit,
  readOnly
}: ShipmentFormProps) {
  const {
    register,
    handleSubmit,
    formState: { errors: formErrors },
    control,
    reset,
  } = useForm({
    resolver: yupResolver(customRegimeScheme),
  });

  useEffect(() => {
    reset(shipment);
  }, [shipment, reset]);

  const handleFormSubmit = (shipment: ShipmentProps) => {
    onSubmit(shipment);
  };

  const handleCancel = () => {
    reset({ DSC_INSTRUCAO: "" });
    onCancel();
  };

  return (
    <form
      style={{ width: "100%" }}
      onSubmit={handleSubmit(handleFormSubmit as any)}
      data-testid="customRegime-form"
    >
      {errors.map((error) => (
        <Alert key={error} severity="error" style={{ marginBottom: 20 }}>
          {error}
        </Alert>
      ))}
      <Grid
        data-testid="search"
        container
        spacing={2}
        marginTop={1}
        border={1}
        padding={2}
        borderColor={"#7b7b7b"}
        borderRadius={2}
        alignItems="center"
      >
        <Grid marginBottom={5} container justifyContent="flex-end" spacing={2}>
          <Grid item xs={5}>
            <Controller
              defaultValue={shipment.GC_ID_CENTRO || undefined}
              control={control}
              render={({ field: { value, ref, onChange, ...field } }) => (
                <>
                  <Autocomplete
                    disabled={controlEdit.isInUse || readOnly}
                    options={center || []}
                    getOptionLabel={(option) => option.description}
                    value={
                      center?.findLast((item) => item.id === value) || null
                    }
                    renderInput={(params: any) => (
                      <TextField
                        {...params}
                        {...field}
                        disabled={controlEdit.isInUse || readOnly}
                        label="Company - Plant"
                        size="small"
                        inputRef={ref}
                        error={!!formErrors.ID_CENTRO}
                        variant="outlined"
                      />
                    )}
                    onChange={(_: any, data: any) => onChange(data?.id)}
                  />
                </>
              )}
              name="ID_CENTRO"
            />
          </Grid>
          <Grid item xs={4}>
            <Controller
              control={control}
              render={({ field: { value } }) => (
                <TextField
                  fullWidth
                  disabled={controlEdit.isInUse || readOnly}
                  type="text"
                  label="Description"
                  size="small"
                  variant="outlined"
                  defaultValue={value}
                  error={!!formErrors.DSC_INSTRUCAO}
                  InputLabelProps={{ shrink: !!value }}
                  {...register("DSC_INSTRUCAO")}
                  inputProps={{ maxLength: 200 }}
                  multiline
                  maxRows={6}
                />
              )}
              name="DSC_INSTRUCAO"
            />
          </Grid>
          <Grid item xs={3}>
            <Controller
              render={({ field: { value } }) => (
                <FormControlLabel
                  control={
                    <Switch
                      checked={!!value}
                      disabled={readOnly}
                      inputProps={{ "aria-label": "controlled" }}
                      {...register("ATIVO")}
                    />
                  }
                  labelPlacement="start"
                  label="Status"
                />
              )}
              name="ATIVO"
              control={control}
            />
          </Grid>
          <Grid item xs={12}></Grid>
        </Grid>
        <Grid container justifyContent="flex-end" spacing={2}>
          <Grid>
            <Button
              variant="outlined"
              onClick={handleCancel}
              disabled={isLoading}
              data-testid="cancel-button"
            >
              Cancel
            </Button>
          </Grid>
          {!readOnly ? (
              <Grid marginLeft={2}>
                <Button
                    variant="contained"
                    disabled={isLoading}
                    type="submit"
                    data-testid="submit-button"
                >
                  Confirm
                </Button>
              </Grid>
          ) : null }
        </Grid>
      </Grid>
    </form>
  );
}
